import { createContext, Fragment, useContext, useEffect } from 'react';
import { Route, useLocation, useParams } from 'react-router-dom';
import { forEach, isArray, map } from 'lodash';
import { string } from 'prop-types';

import { ADMIN_PAGE_ID_PROPERTY_OVERRIDES, AdminPages, DEFAULT_ID_PROPERTY } from './AdminPages';
import { useUserId } from '../../stores/SessionStore';
import { useSessionStorageJson } from '../../utilities/StorageUtilities';
import { buildPageUrl, buildRootPageTarget } from './AdminLinkUtils';
import { useNavigate } from '../../utilities/PgmUtilities';
import AuthRoleGuard from '../auth/AuthRoleGuard';

export function LastAdminPageRedirect(props) {
    const { defaultPage } = props;
    const [lastAdminPageState] = useLastAdminPageState();
    const navigate = useNavigate();

    useEffect(() => {
        // If we're at "/admin" and need a place to go...
        if (!lastAdminPageState) {
            navigate(buildRootPageTarget(defaultPage), { replace: true });

            return;
        }

        const { name: pageName, params: pageParams } = lastAdminPageState;

        const adminPageUrl = buildPageUrl(pageName, pageParams);

        navigate(adminPageUrl, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // [] = only run once...

    return null;
}

LastAdminPageRedirect.propTypes = {
    defaultPage: string.isRequired,
};

const lastAdminPageKeyContext = createContext();
const useLastPageRedirectKey = () => {
    const key = useContext(lastAdminPageKeyContext);

    if (!key) {
        throw new Error('To use redirects, you must have a LastPageRedirectProvider parent');
    }

    return key;
};

export function LastAdminPageRedirectProvider({ children, pageKey }) {
    return <lastAdminPageKeyContext.Provider value={pageKey}>{children}</lastAdminPageKeyContext.Provider>;
}

export function buildListDetailAdminRoute(url, ListComponent, DetailComponent, options) {
    const {
        idProperty: idPropOption,
        listProps = {},
        detailProps = {},
        newProps = {},
        additionalRoutes,
    } = options || {};

    const idProperty = ADMIN_PAGE_ID_PROPERTY_OVERRIDES[idPropOption] || DEFAULT_ID_PROPERTY;

    return (
        <Route path={`${url}/*`} className="flex-container" key={url}>
            <RouteWithPersistence path="/" element={<ListComponent {...listProps} />} />
            <RouteWithPersistence path="new" element={<DetailComponent {...newProps} />} />
            <RouteWithPersistence path={`:${idProperty}`} element={<DetailComponent {...detailProps} />} />
            {additionalRoutes &&
                isArray(additionalRoutes) &&
                map(additionalRoutes, (additionalRoute, i) => <Fragment key={i}>{additionalRoute}</Fragment>)}
        </Route>
    );
}

export function buildTabbedAdminRoute(url, PageComponent, options = {}) {
    const { defaultTab, validTabs, pageProps = {}, additionalRoutes } = options;

    function TabGuard() {
        const { tab } = useParams();
        const navigate = useNavigate();

        useEffect(() => {
            if (!tab) {
                navigate(`${defaultTab}`, { replace: true });
                return;
            }
            if (validTabs && !validTabs.includes(tab)) {
                navigate(`../${defaultTab}`, { replace: true });
            }
        }, [tab, navigate]);

        if (!tab || (validTabs && !validTabs.includes(tab))) {
            return null;
        }
        return <PageComponent {...pageProps} />;
    }

    return (
        <Route path={`${url}/*`} key={url}>
            <Route index element={<TabGuard />} />
            <Route path=":tab" element={<TabGuard />} />
            {additionalRoutes &&
                isArray(additionalRoutes) &&
                map(additionalRoutes, (additionalRoute, i) => <Fragment key={i}>{additionalRoute}</Fragment>)}
        </Route>
    );
}

export function buildAdminRoute(url, Component) {
    return (
        <Route path={`${url}/*`} className="flex-container" key={url}>
            <RouteWithPersistence path="/" element={<Component />} />
        </Route>
    );
}

export function buildRedirectAdminRoute(url, targetElement, options = {}) {
    const { key = url } = options;

    return (
        <Route path={`${url}/*`} className="flex-container" key={key}>
            <RouteWithPersistence path="/" element={targetElement} />
        </Route>
    );
}

export function buildVoipAdminRoute(url, components, options = {}) {
    const {
        indexComponent: IndexComponent,
        listComponent: ListComponent,
        detailComponent: DetailComponent,
        callManagersListComponent: CallManagersListComponent,
        callManagersDetailComponent: CallManagersDetailComponent,
    } = components;
    const { idProperty = DEFAULT_ID_PROPERTY, key = url } = options;

    return (
        <Route path={`${url}/*`} className="flex-container" key={key}>
            <RouteWithPersistence path="/" element={<IndexComponent />} />
            <RouteWithPersistence path="list" element={<ListComponent />} />
            <Route path="callManagers/*" className="flex-container">
                <RouteWithPersistence path="/" element={<CallManagersListComponent />} />
                <RouteWithPersistence path="new" element={<CallManagersDetailComponent />} />
                <RouteWithPersistence path={`:${idProperty}`} element={<CallManagersDetailComponent />} />
            </Route>
            <RouteWithPersistence path={`:${idProperty}`} element={<DetailComponent />} />
        </Route>
    );
}

export function buildSysAdminRoute(url, Component) {
    return (
        <AuthRoleGuard allowedRoles={['super']} key={url}>
            <Route path={`${url}/*`} className="flex-container">
                <RouteWithPersistence path="/" element={<Component />} />
            </Route>
        </AuthRoleGuard>
    );
}

export function RouteWithPersistence(props) {
    const { path, element } = props;

    return <Route path={path} element={<AdminRouteLastState element={element} />} />;
}

export function AdminRouteLastState(props) {
    const { element } = props;
    const params = useParams(); // contains "_id" for example
    const { pathname } = useLocation(); // pathname
    const [, setLastAdminPage] = useLastAdminPageState();

    useEffect(() => {
        forEach(AdminPages, (adminPage) => {
            const reactUrl = buildRootPageTarget(adminPage);

            if (pathname.indexOf(reactUrl) === 0) {
                setLastAdminPage({ name: adminPage, params });
                return false;
            }
        });
    }, [params, pathname, setLastAdminPage]);

    return element;
}

export function useLastAdminPageState() {
    const [userId] = useUserId();
    const prefix = useLastPageRedirectKey();
    const key = `${prefix}-${userId}`;

    return useSessionStorageJson(key);
}
